import { memo, useState, useCallback, useRef } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import MediaLightbox from '../MediaLightbox';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import { Delete, CloudUpload } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { HANDLES, type UploadNodeData, useCanvasStore } from '../useCanvasStore';
import { studioService } from '../../../../services/studio.service';
import { useStudioStore } from '../../stores/studio.store';
import { getAssetSrc } from '../../utils/assetUrl';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';

function UploadImageNode({ id, data }: NodeProps) {
  const d = data as UploadNodeData;
  const { enqueueSnackbar } = useSnackbar();
  const { updateNodeData, removeNode } = useCanvasStore();
  const addAsset = useStudioStore((s) => s.addAsset);
  const projectId = useStudioStore((s) => s.projectId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  const handleUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Only image files are supported', { variant: 'warning' });
      return;
    }

    updateNodeData(id, { isUploading: true, originalName: file.name });

    try {
      const res = await studioService.uploadImage(file, projectId ?? undefined);

      updateNodeData(id, {
        resultImage: res.image,
        resultMimeType: res.mimeType,
        gcsPath: res.gcsPath,
        assetId: res.assetId,
        isUploading: false,
        originalName: file.name,
      });

      if (res.assetId) {
        addAsset({
          type: 'image',
          data: res.image,
          mimeType: res.mimeType || 'image/png',
          prompt: file.name,
          tool: 'upload',
          serverId: res.assetId,
          gcsPath: res.gcsPath,
        });
      }

      enqueueSnackbar('Image uploaded!', { variant: 'success' });
    } catch (err) {
      updateNodeData(id, { isUploading: false });
      enqueueSnackbar('Upload failed: ' + (err as Error).message, { variant: 'error' });
    }
  }, [id, projectId, updateNodeData, addAsset, enqueueSnackbar]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const imgSrc = d.resultImage ? getAssetSrc(d.resultImage, d.resultMimeType || 'image/png') : null;

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        minWidth: 260,
        maxWidth: 320,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        className="drag-handle"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: '#9c27b0',
          color: 'white',
          cursor: 'grab',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CloudUpload sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontWeight={600}>
            {d.originalName || 'Upload Image'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => removeNode(id)} sx={{ color: 'white', p: 0.25 }}>
          <Delete sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5 }}>
        {imgSrc ? (
          <Box sx={{ position: 'relative' }}>
            <img
              src={imgSrc}
              alt="Uploaded"
              style={{
                width: '100%',
                borderRadius: 8,
                maxHeight: 200,
                objectFit: 'contain',
                background: '#111',
                cursor: 'pointer',
              }}
              onClick={() => setLightbox(true)}
            />
            <MediaLightbox
              open={lightbox}
              onClose={() => setLightbox(false)}
              src={imgSrc}
              type="image"
            />
            <SaveToLibraryButton assetId={d.assetId} defaultName={d.originalName} />
            <Button
              size="small"
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              sx={{ mt: 1, width: '100%' }}
            >
              Replace
            </Button>
          </Box>
        ) : (
          <Box
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => !d.isUploading && fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: d.isUploading ? 'default' : 'pointer',
              bgcolor: isDragging ? 'action.hover' : 'transparent',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            {d.isUploading ? (
              <CircularProgress size={32} />
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Drop image here or click to browse
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  JPG, PNG, GIF, WebP • Max 20MB
                </Typography>
              </>
            )}
          </Box>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Output handle — only show when we have an image */}
      {d.resultImage && (
        <Handle
          type="source"
          position={Position.Right}
          id={HANDLES.imageOut}
          style={{ width: 10, height: 10, background: '#9c27b0' }}
        />
      )}
    </Box>
  );
}

export default memo(UploadImageNode);
